import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import PillButton from '../../../../../components/input/PillButton';
import Button from '@mui/material/Button';
import { ISeasonFormValues } from '../../../../../@types/model/masterData/season/seasonFormValues';
import { useAppSelector } from '../../../../../@types/redux';
import { ControlledTextField, ControlledMuiAutoCompleteSelect, ControlledDateInput } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { yupResolver } from '@hookform/resolvers/yup';
import SeasonModelHelper from '../../../../../@types/model/masterData/season/seasonModelHelper';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import OptionType from '../../../../../@types/model/optionType';
import CustomReactHookFormDevTools from '../../../../../components/devtools/ReactHookFormDevTools';

interface ISeasonFormProps {
    isLoading : boolean;
    initialValues : ISeasonFormValues;
    onSubmit : (values : ISeasonFormValues) => void;
    onClose : () => void;
}

const SeasonForm = (props : ISeasonFormProps) : React.ReactElement => {
    const commodities = useAppSelector(x => x.masterData.commodities);

    const { handleSubmit, control, formState: { isValid, isDirty }, trigger } = useForm<ISeasonFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(SeasonModelHelper.formSchema()),
        mode: 'onChange',
    });

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const onStartDateChange = () => {
        trigger('endDate');
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const commodityOptions = useMemo(() => {
        return commodities.filter(x => x.isActive)
            .map(OptionType.fromDataModel);
    }, [commodities]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc p20'}>
                    <ControlledTextField
                        className={'mb10'}
                        control={control}
                        label={'Code'}
                        name={'code'}
                        defaultValue={props.initialValues.code}
                        size={'small'}
                    />
                    <ControlledTextField
                        className={'mb10'}
                        control={control}
                        label={'Name'}
                        name={'name'}
                        defaultValue={props.initialValues.name}
                        size={'small'}
                    />
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <ControlledDateInput
                            className={'p0 mb10'}
                            control={control}
                            label={'Start Date'}
                            defaultValue={props.initialValues.startDate}
                            name={'startDate'}
                            onDateChange={onStartDateChange}
                        />
                        <ControlledDateInput
                            className={'p0 mb10'}
                            control={control}
                            label={'End Date'}
                            defaultValue={props.initialValues.endDate ?? undefined}
                            name={'endDate'}
                        />
                    </LocalizationProvider>
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10'}
                        control={control}
                        label={'Commodity'}
                        defaultValues={props.initialValues.commodity}
                        options={commodityOptions}
                        name={'commodity'}
                    />
                    <div className={'flx1 fdr jcfs'}>
                        <ControlledLabelledCheckBox 
                            name={'isActive'}
                            label={'Is Active?'}
                            control={control}
                            defaultValue={props.initialValues.isActive}
                            disabled={props.initialValues.isActive}
                        />
                    </div>
                    <div className={'fdr flx1 aic jcfe mb20'}>
                        <Button 
                            className={'cpd mr20'}
                            onClick={props.onClose} 
                            type={'reset'}>
                            CANCEL
                        </Button>
                        <PillButton
                            className={'pl30 pr30'}
                            text={'SAVE'}
                            size={'small'}
                            type={'submit'}
                            color={'secondary'}
                            disabled={ !isDirty || !isValid || props.isLoading }
                        />
                    </div>
                </div>
            </form>
            <CustomReactHookFormDevTools control={control} />
        </div>
    );
};

export default SeasonForm;
